import React, { FC, useMemo } from 'react';
import { G, Rect, StyleSheet, Svg, Text } from '@react-pdf/renderer';
import { EScoreRating } from '../../../../../services/individualReport';
import { THEME as theme } from '../../../../../styles/theme';

const GAP = 2;
const INSET = 0.5;

const calculateWidth = (value: number, gaps: number) =>
  Math.round(((264 - gaps * GAP - 2 * INSET) * value) / 10) / 10;

const colors: Record<EScoreRating, string[][]> = {
  [EScoreRating.WEAK]: [
    [theme.colors.accent.red[100], theme.colors.accent.red[10]],
    [theme.colors.accent.blue[30], theme.colors.accent.blue[100]],
    [theme.colors.accent.green[30], theme.colors.accent.green[100]],
  ],
  [EScoreRating.NORMAL]: [
    [theme.colors.accent.red[30], theme.colors.accent.red[100]],
    [theme.colors.accent.blue[100], theme.colors.accent.blue[10]],
    [theme.colors.accent.green[30], theme.colors.accent.green[100]],
  ],
  [EScoreRating.STRONG]: [
    [theme.colors.accent.red[30], theme.colors.accent.red[100]],
    [theme.colors.accent.blue[30], theme.colors.accent.blue[100]],
    [theme.colors.accent.green[100], theme.colors.accent.green[10]],
  ],
};

const styles = StyleSheet.create({
  text: {
    fontFamily: 'Roboto-Bold',
    fontSize: 8,
  },
});

interface IGroupsDistribution {
  distribution: number[];
  score_rating: EScoreRating;
  code: string;
}

const GroupsDistribution: FC<IGroupsDistribution> = ({ distribution, score_rating, code }) => {
  const INVERTED_CODES = new Set(['C9', 'T12', 'T9']);

  const shouldInvert = (code: string) => INVERTED_CODES.has(code);

  const getColorIndex = (index: number) => {
    if (!shouldInvert(code)) return index;

    if (index === 0) return 2;
    if (index === 2) return 0;
    return 1;
  };

  const normalizedDistribution = useMemo(() => {
    if (!shouldInvert(code)) return distribution;

    return [distribution[2], distribution[1], distribution[0]];
  }, [distribution, code]);

  const rects = useMemo(
    () =>
      normalizedDistribution.map((value, index, array) => {
        const gaps = array.length - array.filter(value => value === 0).length - 1;
        const width = calculateWidth(value, gaps);

        return {
          x: array.reduce((acc, _, idx) => {
            const DX = gaps > 0 && array[idx] > 0 ? GAP : 0;

            return idx < index ? acc + calculateWidth(array[idx], gaps) + DX : acc;
          }, INSET),
          width,
          value,
        };
      }),
    [normalizedDistribution]
  );

  return (
    <Svg width="264" height="16" viewBox="0 0 264 16" fill="none">
      {rects.map(({ x, width, value }, index) => {
        const colorIndex = getColorIndex(index);
        const [background, text] = colors[score_rating][colorIndex];

        const TEXT_INSET = value < 10 ? 2 : 4;

        return (
          <G key={index}>
            <Rect y="0" height="16" rx="3.5" ry="3.5" fill={background} {...{ x, width }} />
            {value >= 5 ? (
              <Text
                style={styles.text}
                x={x + (width / 2 - (GAP + TEXT_INSET))}
                y="10.5"
                fill={text}
              >
                {`${value}%`}
              </Text>
            ) : null}
          </G>
        );
      })}
    </Svg>
  );
};

export default GroupsDistribution;
